import { BedDouble } from 'lucide-react';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import type { CleaningNextArrivalResponse } from '@/shared/api';
import { cn } from '@/shared/lib/cn';

interface Props {
  arrival: CleaningNextArrivalResponse | null;
  className?: string;
}

export function cleaningPreparationLabel(
  arrival: CleaningNextArrivalResponse | null,
  t: TFunction,
): string {
  if (!arrival) return t('cleaningPreparation.cleaning.compact.noFollowingStay');
  switch (arrival.cleaningPreparationState) {
    case 'NotConfigured':
      return t('cleaningPreparation.cleaning.compact.notConfigured');
    case 'NotSpecified':
      return t('cleaningPreparation.cleaning.compact.notSpecified');
    case 'LegacyConflict':
      return t('cleaningPreparation.cleaning.compact.legacyConflict');
    case 'Complete':
      return t('cleaningPreparation.cleaning.compact.complete', {
        selection: selectedPlaces(arrival, t),
      });
  }
}

export function CleaningPreparationSummary({ arrival, className }: Props) {
  const { t } = useTranslation();
  const presentation = summaryPresentation(arrival, t);
  return (
    <section
      aria-label={t('cleaningPreparation.cleaning.sectionTitle')}
      className={cn('border-hairline bg-card-alt rounded-control min-w-0 border p-4', className)}
    >
      <div className="flex min-w-0 items-start gap-3">
        <BedDouble className="text-sage mt-0.5 shrink-0" size={19} aria-hidden="true" />
        <div className="min-w-0">
          <h4 className="text-ink break-words text-sm font-semibold">
            {t('cleaningPreparation.cleaning.sectionTitle')}
          </h4>
          <p className={cn('mt-1 break-words text-sm font-semibold', presentation.tone)}>
            {presentation.label}
          </p>
          <p className="text-ink-soft mt-1 break-words text-xs leading-relaxed">
            {presentation.description}
          </p>
        </div>
      </div>
    </section>
  );
}

function summaryPresentation(arrival: CleaningNextArrivalResponse | null, t: TFunction) {
  if (!arrival) {
    return {
      label: t('cleaningPreparation.cleaning.states.noFollowingStay.label'),
      description: t('cleaningPreparation.cleaning.states.noFollowingStay.description'),
      tone: 'text-ink-soft',
    };
  }
  switch (arrival.cleaningPreparationState) {
    case 'NotConfigured':
      return {
        label: t('cleaningPreparation.cleaning.states.notConfigured.label'),
        description: t('cleaningPreparation.cleaning.states.notConfigured.description'),
        tone: 'text-ink-soft',
      };
    case 'NotSpecified':
      return {
        label: t('cleaningPreparation.cleaning.states.notSpecified.label'),
        description: t('cleaningPreparation.cleaning.states.notSpecified.description'),
        tone: 'text-amber',
      };
    case 'LegacyConflict':
      return {
        label: t('cleaningPreparation.cleaning.states.legacyConflict.label'),
        description: t('cleaningPreparation.cleaning.states.legacyConflict.description', {
          selection: storedPlaces(arrival, t),
        }),
        tone: 'text-sealwax',
      };
    case 'Complete':
      return {
        label: selectedPlaces(arrival, t),
        description: t('cleaningPreparation.cleaning.states.complete.description'),
        tone: 'text-sage',
      };
  }
}

function selectedPlaces(arrival: CleaningNextArrivalResponse, t: TFunction): string {
  const places = [
    countLabel(arrival.requestedBedCount, 'cleaningPreparation.bedCount', t),
    countLabel(arrival.requestedSofaBedCount, 'cleaningPreparation.sofaBedCount', t),
  ].filter((label): label is string => label !== null);
  return places.length ? places.join(' · ') : t('cleaningPreparation.noValidSelection');
}

function storedPlaces(arrival: CleaningNextArrivalResponse, t: TFunction): string {
  const beds = storedCount(
    arrival.requestedBedCount,
    'cleaningPreparation.bedCount',
    'cleaningPreparation.bedsNotSpecified',
    t,
  );
  const sofas = storedCount(
    arrival.requestedSofaBedCount,
    'cleaningPreparation.sofaBedCount',
    'cleaningPreparation.sofaBedsNotSpecified',
    t,
  );
  return `${beds} · ${sofas}`;
}

function storedCount(
  count: number | null,
  countKey: string,
  nullKey: string,
  t: TFunction,
): string {
  return count === null ? t(nullKey) : t(countKey, { count });
}

function countLabel(count: number | null, key: string, t: TFunction): string | null {
  if (!count) return null;
  return t(key, { count });
}
